import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APIBaseURLOption } from '../interfaces/api-base-url-option.interface';
import { API_BASE_URL_TOKEN } from '../providers/api-base-url.provider';
import { IUserProfile } from '../interfaces/i-user-profile.interface';
import {IWallet} from "../../../../console/src/app/services/wallet-transaction.service";

export interface IOnboardingStatus {
  transactionPinConfigured: boolean;
  walletConfigured: boolean;
  transactionPinReset: boolean;
  wallets: IWallet[],
}

export interface IFindUserProfile {
  email?: string;
  phoneNumber?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  public readonly url = `${this.baseUrlProvider.rootBase}`;
  public headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
  };

  constructor(
    private readonly httpClient: HttpClient,
    @Inject(API_BASE_URL_TOKEN)
    private readonly baseUrlProvider: APIBaseURLOption,
  ) { }

  public getUserProfileDetails(data: IFindUserProfile) {
    const url = `${this.baseUrlProvider.UIRootBase}/user`;
    let params = new HttpParams();

    if (data.phoneNumber) {
      params = params.append('phoneNumber', data.phoneNumber);
    }

    if (data.email)
      params = params.append('email', data.email);

    return this.httpClient.get<IUserProfile>(
      url,
      {
        params,
        headers: this.headers,
      }
    );
  }

  public sendEmailVerification(email: string, productName: string, destination: string) {
    const url = `${this.url}/send/email-verification`;

    return this.httpClient.post<void>(
      url,
      { email, productName, destination },
      {
        headers: this.headers,
      }
    );
  }

  public completeEmailVerification(email: string, code: string) {
    const url = `${this.url}/complete/email-verification`;

    return this.httpClient.post<void>(
      url,
      { email, code },
      {
        headers: this.headers,
      }
    );
  }

  public completePasswordResetEmail(otp: string, email: string, password: string) {
    const url = `${this.url}/complete/password-reset`;

    return this.httpClient.post<void>(
      url,
      { email, otp, password },
      {
        headers: this.headers,
      }
    );
  }

  public resendPasswordResetEmail(email: string, productName: string, destination: string) {
    const url = `${this.url}/resend/password-reset`;

    return this.httpClient.post<void>(
      url,
      { email, productName, destination },
      {
        headers: this.headers,
      }
    );
  }

  public sendPasswordResetEmail(email: string, productName: string, destination: string) {
    const url = `${this.url}/send/password-reset`;

    return this.httpClient.post<void>(
      url,
      { email, productName, destination },
      {
        headers: this.headers,
      }
    );
  }

  public createUserProfile(idToken: string, productName: string, destination: string) {
    const url = `${this.url}/create/user/profile`;

    return this.httpClient.post<void>(
      url,
      { idToken, productName, destination },
      {
        headers: this.headers,
      }
    );
  }

  public createUserProfileStandalone(body: any, productName: string, destination: string) {
    const url = `${this.url}/create/user/profile`;

    return this.httpClient.post<void>(
      url,
      { ...body, productName, destination },
      {
        headers: this.headers,
      }
    );
  }

  public getMyProfileDetails() {
    const url = `${this.baseUrlProvider.UIRootBase}/user/mine`;

    return this.httpClient.get<IUserProfile>(
      url,
      {
        headers: this.headers,
      }
    );
  }

  public findUserProfileDetails(data: { email?: string, phoneNumber?: string }) {
    const url = `${this.baseUrlProvider.rootBase}/user`;

    let params = new HttpParams();

    if (data.email)
      params = params.append('email', data.email);

    if (data.phoneNumber)
      params = params.append('phoneNumber', data.phoneNumber);

    return this.httpClient.get<IUserProfile>(
      url,
      {
        headers: this.headers,
        params,
      }
    );
  }

  public changeTransactionPin(data: { old: string, next: string }) {
    const url = `${this.baseUrlProvider.rootBase}/ashgrid/user/transaction-pin/change`;

    return this.httpClient.put(
      url,
      data,
      {
        headers: this.headers,
      }
    );
  }

  public completeOnboarding(data: { countryId: string, pin: string }) {
    const url = `${this.baseUrlProvider.rootBase}/ashgrid/user/complete-onboarding`;

    return this.httpClient.put(
      url,
      data,
      {
        headers: this.headers,
      }
    );
  }

  public onboardingStatus() {
    const url = `${this.baseUrlProvider.rootBase}/ashgrid/user/onboarding-status`;

    return this.httpClient.get<IOnboardingStatus>(
      url,
      {
        headers: this.headers,
      }
    );
  }
}
